/*
  PDP purchase options — arithmetic + the <rb-purchase-options> custom element.

  Money is integer cents from Shopify (variant.price, selling_plan_allocation.price)
  end to end; nothing here re-derives a price from a percentage or a cadence.
  A cadence is a delivery interval, never a supply of days or servings.

  The arithmetic half runs in Node (see shopify-theme/tests/rb-purchase-options.test.cjs);
  every browser API touch sits behind `typeof window`.

  Submission stays Dawn's: the controls this element renders are real
  form-associated inputs (`form="product-form-<section id>"`) named `id`,
  `quantity` and `selling_plan`, so the checked radio is what posts — with or
  without JavaScript. This file only keeps the *displayed* numbers equal to the
  numbers those controls will submit.
*/
(function () {
  'use strict';

  var DEFAULT_MONEY_FORMAT = '${{amount}}';

  // Shopify's money_format placeholders and the separators each one implies.
  var MONEY_RULES = {
    amount: { precision: 2, thousands: ',', decimal: '.' },
    amount_no_decimals: { precision: 0, thousands: ',', decimal: '.' },
    amount_with_comma_separator: { precision: 2, thousands: '.', decimal: ',' },
    amount_no_decimals_with_comma_separator: { precision: 0, thousands: '.', decimal: ',' },
    amount_with_apostrophe_separator: { precision: 2, thousands: "'", decimal: '.' },
    amount_no_decimals_with_space_separator: { precision: 0, thousands: ' ', decimal: ',' },
    amount_with_space_separator: { precision: 2, thousands: ' ', decimal: ',' },
    amount_with_period_and_space_separator: { precision: 2, thousands: ' ', decimal: '.' }
  };

  function wholeCents(value, name) {
    if (value === undefined || value === null) return 0;
    if (typeof value !== 'number' || !isFinite(value) || Math.floor(value) !== value || value < 0) {
      throw new RangeError(name + ' must be a non-negative whole number of cents');
    }
    return value;
  }

  /*
    calculateOffer — the only place a displayed total is produced.

    totalCents      what Shopify will charge for this line right now
    comparisonCents the same quantity bought once, so a saving is truthful
    savingCents     never negative: a dearer subscription shows no saving
    perServingCents null unless a verified servings figure was supplied
    perDayCents     null unless the unit's days of supply were supplied (the set)
  */
  function calculateOffer(options) {
    var opts = options || {};
    var quantity = opts.quantity;

    if (typeof quantity !== 'number' || !isFinite(quantity) || Math.floor(quantity) !== quantity || quantity < 1) {
      throw new RangeError('quantity must be a positive integer');
    }

    var unit = wholeCents(opts.unitPriceCents, 'unitPriceCents');
    var once = wholeCents(opts.oneTimeUnitPriceCents, 'oneTimeUnitPriceCents');
    var servings = Number(opts.servingsPerUnit);
    if (!isFinite(servings) || servings <= 0) servings = 0;
    var days = Number(opts.daysPerUnit);
    if (!isFinite(days) || days <= 0) days = 0;

    var totalCents = unit * quantity;
    var comparisonCents = once * quantity;

    return {
      totalCents: totalCents,
      comparisonCents: comparisonCents,
      savingCents: Math.max(0, comparisonCents - totalCents),
      // Servings are per unit, so the divisor grows with the order. A delivery
      // interval is never part of this.
      perServingCents: servings > 0 ? totalCents / (servings * quantity) : null,
      perDayCents: days > 0 ? totalCents / (days * quantity) : null
    };
  }

  /* Mirrors Liquid's `money_without_trailing_zeros` for the shop's money_format. */
  function formatMoney(cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) value = 0;

    var template = typeof format === 'string' && format.indexOf('{{') > -1 ? format : DEFAULT_MONEY_FORMAT;
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    var rule = (match && MONEY_RULES[match[1]]) || MONEY_RULES.amount;

    var fixed = (value / 100).toFixed(rule.precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, rule.thousands);
    var fraction = parts[1] || '';
    var amount = fraction && !/^0+$/.test(fraction) ? whole + rule.decimal + fraction : whole;

    return template.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /*
    intervalMonths / intervalLabel — the JS mirror of snippets/rb-plan-interval.liquid
    and the interval-suffix half of snippets/rb-plan-price.liquid. Cadence word
    order is app-specific ("1 month" from the custom group, "Deliver every 2
    months" from Shopify Subscriptions), so the interval is the first numeric
    word immediately followed by a month or months unit — never simply the first
    word, and never a week-denominated cadence (no weekly plan is sold; a
    "2 weeks" value is unreadable, same as any other value with no whole
    number of months).

    Shopify's own auto-generated name for a native Selling Plan Group's
    1-month interval omits the count entirely ("Deliver every month", never
    "Deliver every 1 month") — a bare "month" (singular, no digit anywhere)
    means 1, same as an explicit "1 month" would. Only that exact singular
    word stands in for the missing digit; "months" (plural) with no number
    in front of it is genuinely ambiguous data, not a 1-month guess.
  */
  function intervalMonths(optionValue) {
    var words = typeof optionValue === 'string' ? optionValue.split(' ') : [];
    for (var i = 0; i < words.length; i++) {
      var n = Number(words[i]);
      if (isFinite(n) && n > 0) {
        var unit = (words[i + 1] || '').toLowerCase();
        return unit.indexOf('month') > -1 ? n : 0;
      }
    }
    for (var j = 0; j < words.length; j++) {
      if (words[j].toLowerCase() === 'month') return 1;
    }
    return 0;
  }

  /* "$price" + this suffix is the whole displayed plan price — never a guessed default. */
  function intervalLabel(optionValue) {
    var months = intervalMonths(optionValue);
    if (months === 1) return '/mo';
    if (months > 1) return '/' + months + ' mo';
    return 'per delivery';
  }

  /*
    mapSetsToPlans — the rows buy box's one rule: a row is an N-month supply on
    the plan that bills every N months, and it ships N ÷ supplyMonths units
    (supplyMonths = how many months one unit lasts, custom.supply_months). A plan
    whose months do not divide evenly by supplyMonths is not a row: a 3-month
    bottle has no 1- or 2-month supply.

    A row therefore exists only because the store sells a plan on that interval:
    the 3-month row appears because a 3-month allocation is live, and disappears
    the moment it is not. Nothing here knows a plan id, a percentage or a
    catalogue; it reads what the selected variant was actually allocated.

    allocations  [{planId, priceCents, interval}] — the selected variant's own
    maxSets      longest plan, in months, the buy box offers
    supplyMonths months one unit lasts; anything but a positive integer means 1
    returns      [{months, units, planId, unitPriceCents}] in ascending month
                 order, with a gap where an interval is missing (never renumbered)
  */
  function mapSetsToPlans(allocations, maxSets, supplyMonths) {
    if (typeof maxSets !== 'number' || !isFinite(maxSets) || Math.floor(maxSets) !== maxSets || maxSets < 1) {
      throw new RangeError('maxSets must be a positive integer');
    }

    var supply = supplyMonths;
    if (typeof supply !== 'number' || !isFinite(supply) || Math.floor(supply) !== supply || supply < 1) supply = 1;

    var list = Array.isArray(allocations) ? allocations : [];
    var rows = [];

    for (var months = supply; months <= maxSets; months += supply) {
      for (var i = 0; i < list.length; i++) {
        var allocation = list[i] || {};
        if (Number(allocation.interval) !== months) continue;
        rows.push({
          months: months,
          units: months / supply,
          planId: String(allocation.planId),
          unitPriceCents: allocation.priceCents
        });
        break;
      }
    }

    return rows;
  }

  /*
    bestValueIndex — which row may claim "Best value", by price per set and only
    when that is strictly the lowest. A tie has no best; a lone row has nothing
    to be better than. -1 means no badge, which is the honest default.
  */
  function bestValueIndex(rows) {
    var list = Array.isArray(rows) ? rows : [];
    if (list.length < 2) return -1;

    var best = -1;
    var lowest = Infinity;
    var tied = false;

    for (var i = 0; i < list.length; i++) {
      var price = Number((list[i] || {}).unitPriceCents);
      if (!isFinite(price)) continue;
      if (price < lowest) {
        lowest = price;
        best = i;
        tied = false;
      } else if (price === lowest) {
        tied = true;
      }
    }

    return tied ? -1 : best;
  }

  /*
    renewLine — the rows buy box's renewal sentence, up to its full stop. The
    count is the interval in months; shipping is measured against the free
    shipping threshold (cents, 0 = no free shipping) the same way paintShip
    measures it, else the flat rate is named.
  */
  function renewLine(count, totalText, totalCents, thresholdCents, flatText) {
    var every = count === 1 ? 'month' : count + ' months';
    var free = thresholdCents > 0 && totalCents >= thresholdCents;
    var ship = free ? ', free US shipping' : ' and ' + flatText + ' shipping';
    return 'Renews every ' + every + ' at ' + totalText + ' plus tax' + ship + '.';
  }

  /*
    sumPairRows — the Daily System's rows when the set is sold as its components
    (custom.components): each component posts its own line on its own plan from
    the group named "Daily System", so a row is the sum of the components'
    allocations on that interval. A row needs every component to have one; a
    component missing an interval drops that row (never priced off half a set).

    componentAllocs [{variantId, priceCents, allocations: [{planId, priceCents, interval}]}]
    supplyMonths    months one set lasts; anything but a positive integer means 1
    discountCents   off the one-time pair (the automatic "buy once" discount),
                    worked out in Liquid as components' sum − the set's price
    returns         {rows: [{months, units, planIds, planId, unitPriceCents, total}],
                     once: one-time cents for one set}
  */
  function sumPairRows(componentAllocs, supplyMonths, discountCents, maxSets) {
    var parts = Array.isArray(componentAllocs) ? componentAllocs : [];
    var max = typeof maxSets === 'number' && maxSets >= 1 ? Math.floor(maxSets) : 3;
    var supply = supplyMonths;
    if (typeof supply !== 'number' || !isFinite(supply) || Math.floor(supply) !== supply || supply < 1) supply = 1;

    var sum = 0;
    parts.forEach(function (part) {
      sum += wholeCents((part || {}).priceCents, 'priceCents');
    });
    var off = Number(discountCents);
    if (!isFinite(off) || off < 0) off = 0;

    var rows = [];
    if (parts.length) {
      for (var months = supply; months <= max; months += supply) {
        var unit = 0;
        var planIds = [];
        for (var p = 0; p < parts.length; p++) {
          var list = Array.isArray(parts[p] && parts[p].allocations) ? parts[p].allocations : [];
          var hit = null;
          for (var i = 0; i < list.length; i++) {
            if (list[i] && Number(list[i].interval) === months) {
              hit = list[i];
              break;
            }
          }
          if (!hit) {
            planIds = null;
            break;
          }
          unit += wholeCents(hit.priceCents, 'priceCents');
          planIds.push(String(hit.planId));
        }
        if (!planIds) continue;
        var units = months / supply;
        rows.push({
          months: months,
          units: units,
          planIds: planIds,
          planId: planIds.join(','),
          unitPriceCents: unit,
          total: unit * units
        });
      }
    }

    return { rows: rows, once: Math.max(0, sum - off) };
  }

  /* savingPercent — "25% off": the saving against the one-time total of the
     same units, rounded to a whole percent (0 when there is no saving). */
  function savingPercent(offer) {
    var o = offer || {};
    if (!(o.comparisonCents > 0) || !(o.savingCents > 0)) return 0;
    return Math.round((o.savingCents * 100) / o.comparisonCents);
  }

  /* dayText — the per-day half of the price pill. Under $1 it reads in cents
     ("16¢ a day"), else money ("$1.57 a day"); null when there is no figure. */
  function dayText(perDayCents, format) {
    if (perDayCents === null || perDayCents === undefined || !isFinite(perDayCents) || perDayCents <= 0) return null;
    var cents = Math.round(perDayCents);
    return (cents < 100 ? cents + '\u00a2' : formatMoney(cents, format)) + ' a day';
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      savingPercent: savingPercent,
      dayText: dayText,
      sumPairRows: sumPairRows,
      calculateOffer: calculateOffer,
      formatMoney: formatMoney,
      intervalLabel: intervalLabel,
      mapSetsToPlans: mapSetsToPlans,
      bestValueIndex: bestValueIndex,
      renewLine: renewLine
    };
  }

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;

  function intervalSuffix(months) {
    if (!months || months === 1) return ' / month';
    return ' / ' + months + ' months';
  }

  function setPrice(element, total, comparison) {
    if (!element) return;
    element.textContent = total;
    if (!comparison) return;
    var struck = document.createElement('s');
    struck.className = 'rb-price__was';
    struck.textContent = comparison;
    element.appendChild(document.createTextNode(' '));
    element.appendChild(struck);
  }

  function show(element, visible) {
    if (element) element.hidden = !visible;
  }

  var RbPurchaseOptions = class extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId || '';
      this.sectionId = this.dataset.sectionId || '';
      this.moneyFormat = this.dataset.moneyFormat || DEFAULT_MONEY_FORMAT;
      this.servings = parseInt(this.dataset.servings, 10) || 0;
      this.days = parseInt(this.dataset.days, 10) || 0;
      this.freeShippingCents = parseInt(this.dataset.freeShipping, 10) || 0;
      this.unitOne = this.dataset.unitLabelOne || '';
      this.shipFlat = this.dataset.shipFlat || '';
      this.unitMany = this.dataset.unitLabelMany || '';
      // Bundle buy box: rows are set counts and the set count picks the cadence.
      this.setMode = this.dataset.setMode === 'true';
      this.maxSets = parseInt(this.dataset.maxSets, 10) || 3;
      this.variants = this.readVariants();
      // Months one unit lasts (custom.supply_months); blank or junk is 1.
      this.supplyMonths = parseInt(this.variants.supplyMonths, 10) || 1;
      delete this.variants.supplyMonths;
      // The set sold as its components (custom.components): one line each.
      this.pair = this.variants.pair || null;
      delete this.variants.pair;

      this.boundSync = this.sync.bind(this);
      this.onDocumentChange = function (event) {
        if (this.owns(event.target)) this.sync();
      }.bind(this);
      this.onDocumentClick = function (event) {
        var toggle = event.target.closest && event.target.closest('[data-rb-po-once-toggle]');
        if (!toggle || toggle.getAttribute('data-form-id') !== this.formId) return;
        this.dataset.rbPoMode = this.dataset.rbPoMode === 'once' ? 'subscribe' : 'once';
        this.sync();
      }.bind(this);

      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('input', this.onDocumentChange);
      document.addEventListener('click', this.onDocumentClick);

      // Dawn replaces the price block *after* it rewrites the variant input, so
      // the post-render pubsub event is the only safe moment to repaint it.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.boundSync);
      }

      // Compatibility contract: snippets/rb-sticky-atc.liquid seeds itself with this.
      window.rbPlanSync = this.boundSync;

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('input', this.onDocumentChange);
      document.removeEventListener('click', this.onDocumentClick);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      if (window.rbPlanSync === this.boundSync) window.rbPlanSync = undefined;
    }

    readVariants() {
      var node = this.querySelector('script[data-rb-po-variants]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    /* Every control that can move the price, wherever it sits in the DOM. */
    owns(target) {
      if (!target || !target.getAttribute) return false;
      if (target.classList && target.classList.contains('product-variant-id')) return true;
      var name = target.getAttribute('name');
      if (name !== 'quantity' && name !== 'selling_plan') return false;
      return target.getAttribute('form') === this.formId || this.contains(target);
    }

    planRadios() {
      return Array.prototype.slice.call(
        document.querySelectorAll('input[name="selling_plan"][form="' + this.formId + '"]')
      );
    }

    submitButton() {
      return document.getElementById('ProductSubmitButton-' + this.sectionId);
    }

    currentVariant() {
      if (this.pair) {
        // The set has no plans of its own; its one-time price is the pair's.
        var paired = sumPairRows(this.pair.components, this.supplyMonths, this.pair.discount, this.maxSets);
        return { price: paired.once, plans: {}, pairRows: paired.rows };
      }
      var form = document.getElementById(this.formId);
      var input = form ? form.querySelector('input[name="id"]') : null;
      var variant = input ? this.variants[input.value] : null;
      if (variant) return variant;
      var ids = Object.keys(this.variants);
      return ids.length ? this.variants[ids[0]] : { price: 0, plans: {} };
    }

    currentQuantity() {
      var inputs = document.querySelectorAll('input[name="quantity"][form="' + this.formId + '"]');
      var value = 1;
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if (input.type === 'radio') {
          if (input.checked) value = parseInt(input.value, 10);
        } else {
          value = parseInt(input.value, 10);
        }
      }
      if (!isFinite(value) || Math.floor(value) !== value || value < 1) value = 1;
      return value;
    }

    /* Only the selected variant's allocations stay selectable. */
    revalidatePlans(variant) {
      var plans = (variant && variant.plans) || {};
      var checked = null;
      var firstValid = null;
      var oneTime = null;

      this.planRadios().forEach(function (radio) {
        if (radio.value === '') {
          oneTime = radio;
        } else {
          var valid = Object.prototype.hasOwnProperty.call(plans, radio.value);
          radio.disabled = !valid;
          if (valid && !firstValid) firstValid = radio;
          if (!valid && radio.checked) radio.checked = false;
        }
        if (radio.checked) checked = radio;
      });

      if (!checked) {
        checked = firstValid || oneTime;
        if (checked) checked.checked = true;
      }
      return checked;
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    /* The selected variant's allocations, in the shape mapSetsToPlans reads. */
    allocationList(variant) {
      var plans = (variant && variant.plans) || {};
      return Object.keys(plans).map(function (planId) {
        return {
          planId: planId,
          priceCents: plans[planId].price,
          interval: plans[planId].interval
        };
      });
    }

    planInput() {
      return document.querySelector('input[name="selling_plan"][data-rb-po-plan-input][form="' + this.formId + '"]');
    }

    isOneTime() {
      return this.dataset.rbPoMode === 'once';
    }

    /*
      Rows buy box. One hidden `selling_plan` input carries the row's plan id,
      so the cadence can never disagree with the supply that priced the row.
      Rows are keyed by months (data-rb-po-set); each row's quantity radio posts
      its units, which are unique per row, so the posted quantity finds its row.
    */
    syncSets() {
      var variant = this.currentVariant();
      var rows = variant.pairRows || mapSetsToPlans(this.allocationList(variant), this.maxSets, this.supplyMonths);
      var best = bestValueIndex(rows);
      var once = this.isOneTime();

      var byMonths = {};
      var byUnits = {};
      rows.forEach(function (row, index) {
        row.best = index === best;
        byMonths[row.months] = row;
        byUnits[row.units] = row;
      });

      this.paintRows(variant, byMonths, once);

      var row = byUnits[this.currentQuantity()] || rows[0] || null;
      var plan = row && !once ? row : null;

      // The single authoritative plan value; empty is a one-time purchase.
      var input = this.planInput();
      if (input) input.value = plan ? plan.planId : '';

      var quantity = row ? row.units : 1;
      if (this.pair) this.paintItems(row, plan, quantity);
      var offer = calculateOffer({
        unitPriceCents: plan ? plan.unitPriceCents : variant.price,
        oneTimeUnitPriceCents: variant.price,
        quantity: quantity,
        servingsPerUnit: this.servings,
        daysPerUnit: this.days || this.servings
      });

      var total = this.money(offer.totalCents);
      var comparison = offer.savingCents > 0 ? this.money(offer.comparisonCents) : '';

      this.paintTerms(plan ? plan.months : 0, offer, total, 'One-time purchase. No renewal.');
      this.paintOnceToggle(variant, row, once);
      this.paintPrice(total, comparison);
      this.paintPill(offer, row ? row.months : 0);

      var label = this.paintButton(total);
      this.announce(
        plan ? { interval: plan.months } : null,
        quantity,
        total,
        comparison,
        offer,
        plan ? plan.planId : '',
        label
      );
    }

    /* Every row prices its own units on its own plan, in the current mode. */
    paintRows(variant, byMonths, once) {
      var self = this;
      var rowElements = this.querySelectorAll('[data-rb-po-set]');
      var firstAvailable = null;
      var checkedAvailable = false;

      rowElements.forEach(function (element) {
        var months = parseInt(element.dataset.rbPoSet, 10) || 1;
        var row = byMonths[months];
        var input = element.querySelector('.rb-po__set-input');

        // A supply with no allocation is not on sale: hidden and unpostable.
        element.hidden = !row;
        if (input) input.disabled = !row;
        if (!row) {
          if (input) input.checked = false;
          return;
        }

        if (input) {
          if (!firstAvailable) firstAvailable = input;
          if (input.checked) checkedAvailable = true;
        }

        var offer = calculateOffer({
          unitPriceCents: once ? variant.price : row.unitPriceCents,
          oneTimeUnitPriceCents: variant.price,
          quantity: row.units
        });

        var totalEl = element.querySelector('[data-rb-po-set-total]');
        if (totalEl) totalEl.textContent = self.money(offer.totalCents);

        // "$X a month" = row total ÷ plan months, a subscription fact like the cadence.
        var monthlyEl = element.querySelector('[data-rb-po-set-monthly]');
        if (monthlyEl) {
          monthlyEl.textContent = self.money(Math.round(offer.totalCents / row.months)) + ' a month';
          show(monthlyEl, row.months > 1 && !once);
        }

        var wasEl = element.querySelector('[data-rb-po-set-was]');
        if (wasEl) {
          wasEl.textContent = self.money(offer.comparisonCents);
          show(wasEl, offer.savingCents > 0);
        }

        var saveEl = element.querySelector('[data-rb-po-set-save]');
        if (saveEl) {
          saveEl.textContent = savingPercent(offer) + '% off';
          show(saveEl, offer.savingCents > 0);
        }

        // Cadence words and the "Best value" claim are subscription facts; one-time
        // mode prices every row the same way, so neither is true there.
        show(element.querySelector('[data-rb-po-set-cadence]'), !once);
        show(element.querySelector('[data-rb-po-badge]'), Boolean(row.best) && !once);
      });

      if (!checkedAvailable && firstAvailable) firstAvailable.checked = true;
    }

    /* The pair's line inputs (snippets/buy-buttons.liquid): every component
       ships the row's units; on a plan each posts its own plan id, one-time
       posts none (a disabled input is left out of the form data). */
    paintItems(row, plan, quantity) {
      var form = document.getElementById(this.formId);
      if (!form) return;
      form.querySelectorAll('input[data-rb-po-item-qty]').forEach(function (input) {
        input.value = String(quantity);
      });
      form.querySelectorAll('input[data-rb-po-item-plan]').forEach(function (input) {
        var index = parseInt(input.getAttribute('data-rb-po-item-plan'), 10) || 0;
        var id = plan && plan.planIds ? plan.planIds[index] : '';
        input.value = id || '';
        input.disabled = !id;
      });
    }

    paintOnceToggle(variant, row, once) {
      var sets = row ? row.units : 1;
      var oncePrice = this.money(
        calculateOffer({ unitPriceCents: variant.price, oneTimeUnitPriceCents: variant.price, quantity: sets })
          .totalCents
      );
      var planPrice = row
        ? this.money(
            calculateOffer({
              unitPriceCents: row.unitPriceCents,
              oneTimeUnitPriceCents: variant.price,
              quantity: sets
            }).totalCents
          )
        : oncePrice;

      document
        .querySelectorAll('[data-rb-po-once-toggle][data-form-id="' + this.formId + '"]')
        .forEach(function (toggle) {
          var label = toggle.querySelector('[data-rb-po-once-label]');
          var price = toggle.querySelector('[data-rb-po-once-price]');
          if (label) label.textContent = once ? 'Subscribe & save instead' : 'Buy once instead';
          if (price) price.textContent = once ? planPrice : oncePrice;
        });
    }

    sync() {
      if (this.setMode) return this.syncSets();

      var variant = this.currentVariant();
      var checked = this.revalidatePlans(variant);
      var plan = checked && checked.value ? variant.plans[checked.value] : null;
      var quantity = this.currentQuantity();

      var offer = calculateOffer({
        unitPriceCents: plan ? plan.price : variant.price,
        oneTimeUnitPriceCents: variant.price,
        quantity: quantity,
        servingsPerUnit: this.servings,
        daysPerUnit: this.days || this.servings
      });

      var total = this.money(offer.totalCents);
      var comparison = offer.savingCents > 0 ? this.money(offer.comparisonCents) : '';

      this.paintPlans(variant);
      this.paintTerms(plan ? plan.interval : 0, offer, total, 'One-time purchase — ' + total + ' plus tax. No renewal.');
      this.paintOneTime(variant, quantity);
      this.paintPrice(total, comparison);
      this.paintPill(offer, 0);

      var label = this.paintButton(total);
      this.announce(plan, quantity, total, comparison, offer, checked && checked.value ? checked.value : '', label);
    }

    paintPlans(variant) {
      var self = this;
      var plans = (variant && variant.plans) || {};
      var subscribed = false;

      this.querySelectorAll('[data-rb-po-plan]').forEach(function (radio) {
        var plan = plans[radio.value];
        var option = radio.closest('.rb-po__seg-opt');
        var priceEl = option ? option.querySelector('[data-rb-po-seg-price]') : null;
        // The accessible name of a cadence carries what it charges.
        if (priceEl) priceEl.textContent = plan ? ' — ' + self.money(plan.price) + ' per delivery' : '';
        if (radio.checked) subscribed = true;
      });

      var segment = this.querySelector('[data-rb-po-seg]');
      // Inactive, not disabled: the radios stay operable so picking a cadence is
      // how a shopper returns to subscribing.
      if (segment) segment.setAttribute('data-inactive', subscribed ? 'false' : 'true');
    }

    /* The bold part is the renewal (or one-time) sentence; the tail (account
       link, reminder email) is static markup and only shows for a renewal. */
    paintTerms(months, offer, total, onceText) {
      var renew = this.querySelector('[data-rb-po-renew]');
      var tail = this.querySelector('[data-rb-po-tail]');
      if (renew) {
        renew.textContent = months
          ? renewLine(months, total, offer.totalCents, this.freeShippingCents, this.shipFlat)
          : onceText;
      }
      show(tail, Boolean(months));

      this.paintShip(offer);
    }

    paintOneTime(variant, quantity) {
      var price = this.money(
        calculateOffer({
          unitPriceCents: variant.price,
          oneTimeUnitPriceCents: variant.price,
          quantity: quantity
        }).totalCents
      );
      document
        .querySelectorAll('[data-rb-po-once-price][data-form-id="' + this.formId + '"]')
        .forEach(function (element) {
          element.textContent = price;
        });
    }

    paintPrice(total, comparison) {
      var root = document.getElementById('price-' + this.sectionId);
      if (!root) return;
      setPrice(root.querySelector('.price__regular .price-item--regular'), total, comparison);
      setPrice(root.querySelector('.price__sale .price-item--sale'), total, comparison);
      var saleWas = root.querySelector('.price__sale s.price-item--regular');
      if (saleWas) saleWas.style.display = comparison ? 'none' : '';
    }

    /* "Free US shipping" sits in the buy box, or — where the delivery line
       renders (snippets/rb-eta.liquid) — folded into that line instead. */
    paintShip(offer) {
      var free = this.freeShippingCents > 0 && offer.totalCents >= this.freeShippingCents;
      show(this.querySelector('[data-rb-po-ship]'), free);
      document.querySelectorAll('[data-rb-eta-ship]').forEach(function (el) {
        show(el, free);
      });
    }

    /* The price pill (spec §1.3): "$X a month · $Y a day". A month = the
       charged total ÷ the row's months (no rows: no month half); a day =
       offer.perDayCents. Mirrors the first paint in sections/main-product. */
    paintPill(offer, months) {
      var pill = document.querySelector('[data-rb-po-pill]');
      if (!pill) return;
      var monthEl = pill.querySelector('[data-rb-po-pill-month]');
      var dayEl = pill.querySelector('[data-rb-po-pill-day]');
      var sepEl = pill.querySelector('[data-rb-po-pill-sep]');
      var monthText = months > 0 ? this.money(Math.round(offer.totalCents / months)) + ' a month' : null;
      var day = dayText(offer.perDayCents, this.moneyFormat);
      if (monthEl && monthText) monthEl.textContent = monthText;
      if (dayEl && day) dayEl.textContent = day;
      show(monthEl, Boolean(monthText));
      show(dayEl, Boolean(day));
      show(sepEl, Boolean(monthText && day));
      pill.hidden = !(monthText || day);
    }

    paintButton(total) {
      var button = this.submitButton();
      var label = button ? button.querySelector('.rb-atc__label') : null;
      if (!label) return '';
      // buy-buttons.liquid owns the Sold out / Unavailable wording.
      if (!button.disabled) label.textContent = 'Add to cart — ' + total;
      return label.textContent.trim();
    }

    summary(quantity, total, plan) {
      var prefix = '';
      if (this.unitOne) {
        prefix = quantity + ' ' + (quantity === 1 ? this.unitOne : this.unitMany) + ' · ';
      } else if (quantity > 1) {
        prefix = '×' + quantity + ' · ';
      }
      return prefix + total + (plan ? intervalSuffix(plan.interval) : ' one-time');
    }

    announce(plan, quantity, total, comparison, offer, planId, label) {
      var button = this.submitButton();
      document.dispatchEvent(
        new CustomEvent('rb:plan', {
          detail: {
            price: total,
            compare: comparison,
            // Per-day figures were removed from the PDP: no verified days of supply exists.
            perday: '',
            label: label,
            planId: planId,
            soldOut: Boolean(button && button.disabled),
            // Additive keys; the six above are the established contract.
            quantity: quantity,
            summary: this.summary(quantity, total, plan),
            savingCents: offer.savingCents
          }
        })
      );
    }
  };

  if (!customElements.get('rb-purchase-options')) {
    customElements.define('rb-purchase-options', RbPurchaseOptions);
  }
})();
